import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Cascader } from "antd";
import CompletedIcon from "../../../icons/svg/completed.svg";
import CurrentIcon from "../../../icons/svg/current.svg";
import SkippedIcon from "../../../icons/svg/skipped.svg";
import NextIcon from "../../../icons/svg/tick.svg";
import "./OpportunityStatusDrilldown.css";

const ALL_OPTION_VALUE = "all";

export const OpportunityTypeCascader = React.memo(
  ({
    opportunityTypes,
    opportunityTypesLoader,
    form,
    canAddEditOpportunity,
    value,
    onChange,
    allowParentSelection = false, // New flag to control parent node selection
    style = {},
    mode, // ADD or EDIT mode
    currentOpportunityTypeId, // The current opportunity type ID in EDIT mode
    showAllOption = false, // When true, prepends an "All" option and treats it as the default selection
    // When provided and no value is set yet, the cascader auto-selects the
    // opportunity type whose name matches this label (case-insensitive). Runs
    // once per mount: if the user clears the selection later, the default
    // does NOT come back. Use to seed a per-screen preference (e.g. Dashboard
    // defaults to "Sales"); the label is just a string so callers can plug
    // in any future default without touching this component.
    defaultLabel,
    allowClear = true,
  }) => {
    const [cascaderValue, setCascaderValue] = useState([]);
    const [initialValueSet, setInitialValueSet] = useState(false);
    const [defaultApplied, setDefaultApplied] = useState(false);

    // Transform opportunity types to cascader format
    const opportunityTypesCascaderOptions = useMemo(() => {
      if (!opportunityTypes || opportunityTypes.length === 0) return [];

      // Find the current parent in EDIT mode
      let currentParentId = null;
      if (mode === "EDIT" && currentOpportunityTypeId) {
        const currentType = opportunityTypes.find(type => +type.id === +currentOpportunityTypeId);
        if (currentType?.parentType?.id) {
          currentParentId = currentType.parentType.id;
        } else if (currentType) {
          // No valid parentType.id — this type is itself a root parent
          currentParentId = currentType.id;
        }
      }

      // Build parent nodes from two sources:
      // 1. Items with no parentType.id — they ARE root parents
      // 2. Unique parentType objects referenced by child items
      const parentMap = new Map();
      opportunityTypes.forEach(type => {
        if (!type?.id) return; // skip malformed entries
        if (type.parentType?.id) {
          if (!parentMap.has(String(type.parentType.id))) {
            parentMap.set(String(type.parentType.id), type.parentType);
          }
        } else if (!parentMap.has(String(type.id))) {
          parentMap.set(String(type.id), { id: type.id, name: type.name });
        }
      });

      const parentNodes = [...parentMap.values()];

      // Transform parent nodes into cascader format with children
      return parentNodes.map(parent => {
        const children = opportunityTypes
          .filter(type => String(type.parentType?.id) === String(parent.id))
          .map(child => ({
            label: child.name,
            value: child.id,
            key: +child.id,
            isLeaf: true,
          }));

        const isDisabled =
          mode === "EDIT" && currentParentId !== null && +parent.id !== +currentParentId;

        return {
          label: parent.name,
          value: parent.id,
          key: +parent.id,
          children,
          disabled: isDisabled,
          selectable: allowParentSelection || children?.length === 0,
        };
      });
    }, [opportunityTypes, allowParentSelection, mode, currentOpportunityTypeId]);

    // When showAllOption is true, prepend a synthetic "All" option that maps to a null filter.
    const finalCascaderOptions = useMemo(() => {
      if (!showAllOption) return opportunityTypesCascaderOptions;
      return [
        { label: "All", value: ALL_OPTION_VALUE, key: ALL_OPTION_VALUE, isLeaf: true },
        ...opportunityTypesCascaderOptions,
      ];
    }, [opportunityTypesCascaderOptions, showAllOption]);

    // Function to find the cascader path for a given type ID
    const findOpportunityTypePath = useCallback(
      typeId => {
        if (!typeId) return [];

        const opportunityType = opportunityTypes?.find(type => +type.id === +typeId);

        if (!opportunityType) {
          // Not in the flat array — check if it's a parent-only node
          // (exists only inside children's parentType field, e.g. Sales/Service)
          const isParentOnly = opportunityTypes.some(
            type => String(type.parentType?.id) === String(typeId)
          );
          // Always return string IDs so they match cascader option values (which come from API as strings)
          return isParentOnly ? [String(typeId)] : [];
        }

        // Check if this item is also a parent node (other items reference it as parentType)
        const isParentNode = opportunityTypes.some(
          type => type.parentType?.id && String(type.parentType.id) === String(typeId)
        );

        if (isParentNode) {
          return [opportunityType.id];
        }

        // It's a child — return [parentId, childId]
        if (opportunityType.parentType?.id) {
          return [opportunityType.parentType.id, opportunityType.id];
        }

        return [opportunityType.id];
      },
      [opportunityTypes]
    );

    // Set initial value when types are loaded; clear when value is removed
    useEffect(() => {
      if (!value) {
        setCascaderValue(showAllOption ? [ALL_OPTION_VALUE] : []);
        return;
      }
      if (!opportunityTypesLoader && opportunityTypes?.length > 0) {
        const path = findOpportunityTypePath(value);
        if (path.length > 0) {
          setCascaderValue(path);
          if (!initialValueSet) {
            if (form && form !== null) {
              form.setFieldValue("opportunityTypeId", path);
            }
            setInitialValueSet(true);
          }
        }
      }
    }, [
      opportunityTypesLoader,
      opportunityTypes,
      value,
      form,
      findOpportunityTypePath,
      initialValueSet,
      showAllOption,
    ]);

    // Apply `defaultLabel` once per mount: when the list has loaded and the
    // parent hasn't already supplied a value, find the matching type (flat
    // array first, then parent-only refs) and emit it upward. Skipped if the
    // parent has any value, or if we've already applied the default.
    useEffect(() => {
      if (defaultApplied) return;
      if (!defaultLabel) return;
      if (value !== null && value !== undefined && value !== "") {
        setDefaultApplied(true);
        return;
      }
      if (opportunityTypesLoader) return;
      if (!opportunityTypes?.length) return;

      const target = String(defaultLabel).toLowerCase();
      const inFlat = opportunityTypes.find(t => t?.name?.toLowerCase() === target);
      const parentRef = !inFlat
        ? opportunityTypes.find(t => t?.parentType?.name?.toLowerCase() === target)
        : null;
      const matchId = inFlat ? +inFlat.id : parentRef ? +parentRef.parentType.id : null;
      if (matchId !== null) {
        onChange?.(matchId);
        setDefaultApplied(true);
      }
    }, [defaultApplied, defaultLabel, value, opportunityTypesLoader, opportunityTypes, onChange]);

    const handleChange = useCallback(
      newValue => {
        setCascaderValue(newValue);
        if (form && form !== null) {
          form.setFieldValue("opportunityTypeId", newValue);
        }

        if (newValue && newValue.length > 0) {
          const selectedId = newValue[newValue.length - 1];
          onChange(selectedId === ALL_OPTION_VALUE ? null : selectedId);
        } else {
          onChange(null);
        }
      },
      [form, onChange]
    );

    // Custom display render function to handle null values
    const displayRender = useCallback(
      labels => {
        // Show "All" when the synthetic All option is the current selection.
        if (cascaderValue?.length === 1 && cascaderValue[0] === ALL_OPTION_VALUE) {
          return "All";
        }
        // Safely handle null or undefined labels
        if (!labels) {
          return "";
        }

        // Ensure labels is treated as an array
        const safeLabels = Array.isArray(labels) ? labels : [];

        // When no valid labels, use the mapping to show proper text
        // Helper: resolve a label for an ID — checks flat array first, then parentType refs
        const resolveLabel = id => {
          if (!id) return "";
          // In flat array (child items)
          const inFlat = opportunityTypes.find(type => String(type.id) === String(id));
          if (inFlat) return inFlat.name;
          // Parent-only node — lives only in parentType references
          const parentRef = opportunityTypes.find(
            type => String(type.parentType?.id) === String(id)
          );
          return parentRef ? parentRef.parentType.name : String(id);
        };

        // Antd passes raw values as labels when it can't resolve them — detect & fix
        const labelsLookLikeIds =
          safeLabels.length > 0 &&
          cascaderValue.length > 0 &&
          safeLabels.every((lbl, i) => String(lbl) === String(cascaderValue[i]));

        if (safeLabels.length === 0 || labelsLookLikeIds) {
          try {
            return cascaderValue.map(resolveLabel).filter(Boolean).join(" / ");
          } catch (error) {
            return String(cascaderValue);
          }
        }

        return safeLabels.join(" / ");
      },
      [cascaderValue, opportunityTypes]
    );

    return (
      <Cascader
        options={finalCascaderOptions}
        disabled={!canAddEditOpportunity}
        name="opportunityTypeId"
        allowClear={allowClear}
        loading={opportunityTypesLoader}
        placeholder={opportunityTypesLoader ? "Loading..." : "Select opportunity type"}
        changeOnSelect={allowParentSelection}
        expandTrigger="click"
        showSearch
        value={cascaderValue}
        displayRender={displayRender}
        onChange={handleChange}
        style={style}
      />
    );
  }
);

/**
 * Determine the status display icon for a given status node based on status history.
 * Returns { iconSrc, iconAlt, statusClass } or null if icons are disabled.
 */
const getStatusIconDisplay = (statusNode, statusHistory, showStatusIcons, currentStatusId) => {
  if (!showStatusIcons || !statusHistory?.length) return null;

  let iconSrc;
  let iconAlt;
  let statusClass = "";

  // Find the statusHistory entry that matches this node (direct or via parent chain)
  const statusHistoryObject = statusHistory.find(history => {
    if (!history.newStatus) return false;
    if (+history.newStatus.id === +statusNode.id) return true;
    let { parentStatus } = history.newStatus;
    while (parentStatus) {
      if (+parentStatus.id === +statusNode.id) return true;
      ({ parentStatus } = parentStatus);
    }
    return false;
  });

  if (!statusHistoryObject) {
    iconSrc = NextIcon;
    iconAlt = "Upcoming";
    statusClass = "status-upcoming";
  } else if (statusHistoryObject.isTransferred === true) {
    iconSrc = SkippedIcon;
    iconAlt = "Skipped";
    statusClass = "status-skipped";
  } else {
    const currentStatusEntry = statusHistory.find(
      entry => +entry.newStatus?.id === +currentStatusId
    );
    if (currentStatusEntry?.newStatus) {
      let topParent = currentStatusEntry.newStatus;
      while (topParent.parentStatus) {
        topParent = topParent.parentStatus;
      }
      if (
        +topParent.id === +statusNode.id ||
        +statusHistoryObject.newStatus.id === +currentStatusId
      ) {
        iconSrc = CurrentIcon;
        iconAlt = "Current";
        statusClass = "status-current";
      } else {
        iconSrc = CompletedIcon;
        iconAlt = "Completed";
        statusClass = "status-completed";
      }
    } else {
      iconSrc = NextIcon;
      iconAlt = "Upcoming";
      statusClass = "status-upcoming";
    }
  }

  return { iconSrc, iconAlt, statusClass };
};

/**
 * Build a map of statusId -> index of its top-level ancestor in `data`. Used to
 * filter `statusHistory` so entries AFTER the current status are dropped (and
 * therefore render as "upcoming" instead of "completed"/"skipped"). Handles both
 * flat data (`id` + `parentStatusId`) and hierarchical data (`key` + `childs`).
 */
const buildTopAncestorIndexMap = (data, isFlat) => {
  const topAncestorIndexById = new Map();
  if (!Array.isArray(data) || data.length === 0) return topAncestorIndexById;

  if (isFlat) {
    const byId = new Map();
    data.forEach(item => byId.set(String(item.id), item));
    // Index of each root in the input array (roots = no parentStatusId).
    const rootIndexes = new Map();
    data.forEach((item, idx) => {
      if (!item?.parentStatusId) rootIndexes.set(String(item.id), idx);
    });
    data.forEach(item => {
      let cur = item;
      while (cur?.parentStatusId && byId.has(String(cur.parentStatusId))) {
        cur = byId.get(String(cur.parentStatusId));
      }
      const rootIdx = rootIndexes.get(String(cur.id));
      if (rootIdx !== undefined) topAncestorIndexById.set(String(item.id), rootIdx);
    });
  } else {
    data.forEach((node, idx) => {
      const walk = n => {
        if (n?.key !== undefined) topAncestorIndexById.set(String(n.key), idx);
        (n?.childs || []).forEach(walk);
      };
      walk(node);
    });
  }
  return topAncestorIndexById;
};

/**
 * Build cascader options from flat array with parentStatusId relationships
 * or from hierarchical data (backwards compatibility).
 * Optionally renders status icons when statusHistory and showStatusIcons are provided.
 *
 * statusHistory is filtered to drop entries whose top-level ancestor comes AFTER
 * the current status — so a previously-touched-but-now-ahead status renders as
 * upcoming, not completed/skipped (handles backwards transitions).
 */
const buildCascaderOptions = (
  data,
  statusHistory = [],
  showStatusIcons = false,
  currentStatusId = null
) => {
  if (!Array.isArray(data) || data?.length === 0) return [];

  // Check if this is flat data (has 'id' and 'parentStatusId') or hierarchical (has 'key' and 'childs')
  const isFlat =
    data[0] && typeof data[0].id !== "undefined" && typeof data[0].parentStatusId !== "undefined";

  // Sort statusHistory by createTs DESC first so `getStatusIconDisplay`'s
  // `.find()` returns the MOST RECENT matching entry for a node. The server
  // does not guarantee ordering on statusHistory, so without this sort a stale
  // earlier entry can mask a newer one (e.g., a parent touched first via a
  // child with isTransferred=false, then later skipped as a direct transfer
  // with isTransferred=true, would resolve to "completed" instead of "skipped").
  const sortedHistory = [...(statusHistory || [])].sort((a, b) => {
    const ta = new Date(a?.createTs || 0).getTime();
    const tb = new Date(b?.createTs || 0).getTime();
    return tb - ta;
  });

  // Filter statusHistory once: drop entries whose top-level ancestor is AFTER
  // the current status's top-level ancestor in the tree.
  const topAncestorIndexById = buildTopAncestorIndexMap(data, isFlat);
  const currentTopIdx = topAncestorIndexById.has(String(currentStatusId))
    ? topAncestorIndexById.get(String(currentStatusId))
    : null;
  const effectiveHistory =
    !showStatusIcons || currentTopIdx === null
      ? sortedHistory
      : sortedHistory.filter(entry => {
          if (!entry?.newStatus) return false;
          let topParent = entry.newStatus;
          while (topParent.parentStatus) topParent = topParent.parentStatus;
          const idx = topAncestorIndexById.get(String(topParent.id));
          // Unknown status — keep; we don't have enough info to drop it safely.
          if (idx === undefined) return true;
          return idx <= currentTopIdx;
        });

  const renderIconLabel = (name, iconDisplay) => {
    if (!iconDisplay) return name;
    return (
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <img
          src={iconDisplay.iconSrc}
          alt={iconDisplay.iconAlt}
          style={{ width: 14, height: 14, flexShrink: 0 }}
        />
        <span>{name}</span>
      </span>
    );
  };

  if (isFlat) {
    // Handle flat array with parentStatusId relationships
    const idMap = {};
    data?.forEach(item => {
      // Compute icon for ALL items (parent + child) so closed state can show it
      const iconDisplay = getStatusIconDisplay(
        item,
        effectiveHistory,
        showStatusIcons,
        currentStatusId
      );
      idMap[item.id] = {
        label: iconDisplay ? renderIconLabel(item.name, iconDisplay) : item.name,
        textLabel: item.name,
        value: +item.id,
        key: +item.id,
        children: [],
        statusIconDisplay: iconDisplay,
      };
    });

    const cascaderOptions = [];

    data?.forEach(item => {
      if (item.parentStatusId) {
        const parent = idMap[item.parentStatusId];
        if (parent) {
          parent.children.push(idMap[item.id]);
        } else {
          cascaderOptions.push(idMap[item.id]);
        }
      } else {
        cascaderOptions.push(idMap[item.id]);
      }
    });

    const removeEmptyChildren = nodes => {
      nodes.forEach(node => {
        if ((node?.children || []).length === 0) {
          delete node.children;
          node.isLeaf = true;
        } else {
          removeEmptyChildren(node?.children || []);
        }
      });
    };

    removeEmptyChildren(cascaderOptions);

    return cascaderOptions;
  }

  // Handle hierarchical data (backwards compatibility)
  const buildOptions = items => {
    if (!Array.isArray(items)) return [];

    return items.map(item => {
      const iconDisplay = getStatusIconDisplay(
        { id: item.key, name: item.name },
        effectiveHistory,
        showStatusIcons,
        currentStatusId
      );

      return {
        label: iconDisplay ? renderIconLabel(item.name, iconDisplay) : item.name,
        textLabel: item.name,
        value: +item.key,
        key: +item.key,
        children: item.childs && item.childs.length > 0 ? buildOptions(item.childs) : [],
        statusIconDisplay: iconDisplay,
      };
    });
  };

  return buildOptions(data);
};

/**
 * Recursively find the path from root to the given node ID.
 * @param {Array} tree - Hierarchical cascader options.
 * @param {number} targetId - The final ID to find.
 * @returns {Array} Path of IDs (e.g., [parentId, childId])
 */
const findPathToId = (tree, targetId) => {
  let result = [];
  tree?.some(node => {
    if (node.value === targetId) {
      result = [node.value];
      return true;
    }
    if (node.children) {
      const childPath = findPathToId(node.children, targetId);
      if (childPath.length) {
        result = [node.value, ...childPath];
        return true;
      }
    }
    return false;
  });
  return result;
};

/**
 * Final OpportunityStatusCascader component.
 */
export const OpportunityStatusCascader = ({
  leadStatusList,
  canAddEditOpportunity,
  allowParentSelection,
  opportunityStatusLoader,
  value, // single numeric ID, e.g., 2385
  onChange,
  form,
  style = {},
  statusHistory = [], // Status history for showing icons
  showStatusIcons = false, // Flag to enable status icons
  displayRender, // Custom display render function
  size, // Size prop
  className, // Additional CSS classes
  dropdownClassName, // Dropdown CSS classes
  showCheckedStrategy = "SHOW_PARENT", // New prop for checked strategy,
  expandTrigger = "click", // New prop for expand trigger
}) => {
  // Build the tree options with optional status history
  const options = useMemo(
    () => buildCascaderOptions(leadStatusList, statusHistory, showStatusIcons, value),
    [leadStatusList, statusHistory, showStatusIcons, value]
  );

  // Convert single ID to full path for cascader
  const cascaderValue = useMemo(() => {
    if (value) {
      return findPathToId(options, +value);
    }
    return [];
  }, [value, options]);

  // Handle selection changes
  const handleChange = newPath => {
    if (!newPath || !Array.isArray(newPath) || newPath?.length === 0) {
      return;
    }
    const selectedId = newPath?.[newPath?.length - 1];
    if (form) {
      form.setFieldValue("opportunityStatusId", selectedId);
    }
    if (onChange) {
      onChange(selectedId);
    }
  };

  return (
    <Cascader
      allowClear={false}
      options={options}
      disabled={!canAddEditOpportunity}
      name="opportunityStatusId"
      loading={opportunityStatusLoader}
      placeholder={opportunityStatusLoader ? "Loading..." : "Select opportunity status"}
      changeOnSelect={allowParentSelection}
      expandTrigger={expandTrigger}
      showSearch={{
        filter: (inputValue, path) =>
          path.some(
            option =>
              (option.textLabel || (typeof option.label === "string" ? option.label : ""))
                .toLowerCase()
                .indexOf(inputValue.toLowerCase()) > -1
          ),
        render: (inputValue, path) =>
          path.map(option => option.textLabel || option.label).join(" / "),
      }}
      value={cascaderValue}
      onChange={handleChange}
      style={style}
      displayRender={displayRender}
      size={size}
      className={className}
      showCheckedStrategy={
        showCheckedStrategy === "SHOW_CHILD" ? Cascader.SHOW_CHILD : Cascader.SHOW_CHILD
      }
      popupClassName={`opportunity-status-cascader ${dropdownClassName || ""}`}
      getPopupContainer={() => document.body}
    />
  );
};

OpportunityTypeCascader.displayName = "OpportunityTypeCascader";
OpportunityStatusCascader.displayName = "OpportunityStatusCascader";
